import React from "react";
import { connect, ConnectedProps } from "react-redux";

import { GlobalApplicationState } from "globalApplicationState";
import FilledInput from "../forms/inputs/filledInput";
import { AuthoringTinyEditor } from "./authoringTinyMce";
import * as actions from 'modules/posts/actionCreator';
import * as galleryActions from "modules/gallery/actionCreator";

import ImageGalleryDialog from "./dialogs/imageGalleryDialog";
import IntenseEmphasis from "./dialogs/intenseEmphasis";
import { IGalleryItemDetails, ImageHtmlTags } from "modules/gallery";
import { useTinyMceAuthoringLogic } from "modules/common/hooks/useTinyMceAuthoringLogic";
import { ICustomCssModelv1 } from "api/files";
import { PromptTypeEnum } from "modules/smartContent/models";
import { RawEditorOptions } from "tinymce";
import { IPersonalizationToken } from "modules/emails/models";
import InlineImageGalleryWrapper from "./dialogs/inlineImageGalleryWrapper";
import { lcidMappings } from "modules/resources";

interface IBodyv2Props {
    value?: string;
    label?: string;
    editorOptions?: (RawEditorOptions & { selector?: undefined; target?: undefined; });
    activeLcid?: string;
    activeLanguage?: string;
    lcidMappings?: lcidMappings;
    enableInlineImageGallery?: boolean;
    enableSmartContentBodyButton?: boolean;
    enableSmartContentTitleButton?: boolean;
    enableSmartContentSummarizeButton?: boolean;
    postSmartContentEnabled?: boolean;
    fetchingAnalyses?: boolean;
    showSCDropdownButton?: boolean;
    changedSinceSaved?: boolean;
    customCss?: ICustomCssModelv1;
    smartContentSuggestion?: string;
    onFocus?: () => void;
    onFullscreen?: (isFullscreen?: boolean) => void;
    onChange: (body: string) => void;
    onShowSmartContentInsert?: (highlightedText: string, promptType: PromptTypeEnum) => void;
    clearSmartContentSuggestion?: () => void;
    generateAndOpenSmartContent?: (promptTypeForGeneration: PromptTypeEnum, promptTypeForInsert: PromptTypeEnum, body?: string) => void;
    required?: boolean;
    personalizationTokens?: IPersonalizationToken[];
    maxWidth?: number;
    livePreview?: boolean;
    enablePageEmbed?: boolean;
    enableImageGallery?: boolean;
}

const Bodyv2: React.FunctionComponent<PropsWithRedux> = ({
    value,
    label = "Body",
    editorOptions,
    defaultLcid,
    activeLcid,
    lcidMappings,
    fetchingAnalyses = false,
    customCss,
    smartContentSuggestion,
    changedSinceSaved,
    postSmartContentEnabled,
    onFocus,
    onFullscreen,
    onChange,
    grabMentionUsers,
    getImage,
    onShowSmartContentInsert,
    clearSmartContentSuggestion,
    generateAndOpenSmartContent,
    required = true,
    personalizationTokens,
    maxWidth,
    livePreview,
    enablePageEmbed = true,
    enableImageGallery = false
}) => {
    const {
        editorRef,
        intenseEmphasisDialogOpen,
        inlineImageDialogOpen,
        imageGalleryDialogOpen,
        onShowInlineImageDialog,
        onShowImageGalleryDialog,
        onShowIntenseEmphasisDialog,
        onApplyInlineImage,
        onApplyIntenseEmphasis,
        onHideInlineImageDialog,
        onHideImageGalleryDialog,
        onHideIntenseEmphasisDialog,
    } = useTinyMceAuthoringLogic(getImage);

    const [editGalleryId, setEditGalleryId] = React.useState<string | null>(null); //Need to keep the set Id until closed or applied.
    const [editGalleryDetails, setEditGalleryDetails] = React.useState<IGalleryItemDetails[] | null>(null);

    const resetEditGallery = () => {
        setEditGalleryId(null);
        setEditGalleryDetails(null);
    }

    const resetEditDetails = () => {
        setEditGalleryDetails(null);
    }

    const prepareEditInlineImageGallery = (editorRef) => {
        var selectedGallery: Element = editorRef.current.selection.getNode();
        const galleryId = selectedGallery.id;

        //Make sure selection hasn't swapped.
        if (!galleryId.includes("inline-gallery")) {
            return;
        }
        
        const imgs = selectedGallery.querySelectorAll('img[id^="inline-image"]');

        let galleryDetails: IGalleryItemDetails[] = [];
        imgs.forEach(img => {
            let src = img.getAttribute(ImageHtmlTags.Source);
            let ospreyId = img.getAttribute(ImageHtmlTags.OspreyId);
            let caption = img.getAttribute(ImageHtmlTags.ImageCaption) ?? "";

            if (src && ospreyId) {
                let newGalleryItem = {fileUrl: src, ospreyId: ospreyId, caption: caption};
                galleryDetails = galleryDetails.concat(newGalleryItem);
            }
        });

        //Set the selected images to the ones matching the criteria
        setEditGalleryDetails(galleryDetails);
        setEditGalleryId(galleryId);
        onShowImageGalleryDialog();
    }

    return (
        <>
            <FilledInput
                label={label}
                required={required}
                smartContentButtonDisabled={postSmartContentEnabled}
                labelOnly
                value={""}
            />
            <AuthoringTinyEditor
                loading={fetchingAnalyses}
                activeLcid={activeLcid}
                customCss={customCss}
                value={value}
                onChange={onChange}
                defaultLcid={defaultLcid}
                editorRef={editorRef}
                smartContentEnabled={postSmartContentEnabled}
                enableEmbeddedMedia
                enableImageGallery={enableImageGallery}
                enableInlineImages
                onShowInlineImageDialog={onShowInlineImageDialog}
                onShowImageGalleryDialog={onShowImageGalleryDialog}
                onEditImageGallery={prepareEditInlineImageGallery}
                onShowIntenseEmphasisDialog={onShowIntenseEmphasisDialog}
                grabMentionUsers={grabMentionUsers}
                onFocus={onFocus}
                onShowSmartContentInsert={onShowSmartContentInsert}
                changedSinceSaved={changedSinceSaved}
                initOptions={editorOptions}
                onFullscreen={onFullscreen}
                smartContentSuggestion={smartContentSuggestion}
                clearSmartContentSuggestion={clearSmartContentSuggestion}
                generateAndOpenSmartContent={generateAndOpenSmartContent}
                personalizationTokens={personalizationTokens}
                editorMaxWidth={maxWidth}
                livePreview={livePreview}
                enablePageEmbed={enablePageEmbed}
            />

            {
                inlineImageDialogOpen &&
                <ImageGalleryDialog show={inlineImageDialogOpen} allowLink onApply={(selected) => onApplyInlineImage(selected, maxWidth)} onClose={onHideInlineImageDialog} />
            }

            <InlineImageGalleryWrapper
                galleryOpen={imageGalleryDialogOpen} 
                onShowImageGalleryDialog={onShowImageGalleryDialog} 
                onHideImageGalleryDialog={onHideImageGalleryDialog} 
                resetEditGallery={resetEditGallery}
                resetEditDetails={resetEditDetails}
                editorRef={editorRef}
                editGalleryId={editGalleryId}
                editGalleryDetails={editGalleryDetails}
                activeLanguage={lcidMappings[activeLcid ?? ""]?.language ?? ""}
            /> 
            <IntenseEmphasis show={intenseEmphasisDialogOpen} onApply={onApplyIntenseEmphasis} onClose={onHideIntenseEmphasisDialog} />
        </>
    );
}
const connector = connect(
    (state: GlobalApplicationState, ownProps: IBodyv2Props) => ({
        ...ownProps,
        defaultLcid: state.settings.tenantSettings.defaultLCID,
        activeLanguage: state.settings.activeLanguageLCID,
        changedSinceSaved: state.posts.editor.changedSinceSaved,
        lcidMappings: state.resources.lcidMappings
    }),
    {
        grabMentionUsers: actions.getMentionsUsers,
        getImage: galleryActions.getImage
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;
export default connector(Bodyv2);
